import React, { useState, useEffect, useCallback } from 'react';
import { BookmarkIcon } from './Icons';
import { bookmarksAPI } from '../services/api';
import styles from '../styles/ContentCard.module.css';
import videoStyles from '../styles/VideoCard.module.css';

/**
 * Video Card Component
 * Displays course video cards with thumbnail and play button
 *
 * Props:
 * - id: Video ID
 * - videoId: Thinkific video ID
 * - title: Video title
 * - courseName: Course name
 * - description: Video description (optional)
 * - videoUrl: Direct video URL
 * - lessonUrl: Lesson page URL
 * - thumbnailUrl: Video thumbnail URL
 * - onClick: Optional click handler (receives video data)
 * - showBookmark: Show bookmark button (default: true)
 * - isBookmarked: Initial bookmark state (optional, will check API if not provided)
 */
function VideoCard({
  id,
  videoId,
  title,
  courseName,
  description,
  videoUrl,
  lessonUrl,
  thumbnailUrl,
  onClick,
  showBookmark = true,
  isBookmarked: initialIsBookmarked
}) {
  const [isBookmarked, setIsBookmarked] = useState(initialIsBookmarked || false);
  const [isTogglingBookmark, setIsTogglingBookmark] = useState(false);

  const checkBookmarkStatus = useCallback(async () => {
    try {
      const result = await bookmarksAPI.isBookmarked('video', id);
      setIsBookmarked(result.bookmarked);
    } catch (error) {
      console.error('[VideoCard] Failed to check bookmark status:', error);
    }
  }, [id]);

  // Check if bookmarked on mount (only if not provided as prop)
  useEffect(() => {
    if (showBookmark && id && initialIsBookmarked === undefined) {
      checkBookmarkStatus();
    }
  }, [id, showBookmark, initialIsBookmarked, checkBookmarkStatus]);

  // Update local state when prop changes
  useEffect(() => {
    if (initialIsBookmarked !== undefined) {
      setIsBookmarked(initialIsBookmarked);
    }
  }, [initialIsBookmarked]);

  const handleBookmarkClick = async (e) => {
    e.stopPropagation(); // Prevent card click
    if (isTogglingBookmark) return;

    setIsTogglingBookmark(true);
    try {
      const result = await bookmarksAPI.toggleBookmark('video', id);
      setIsBookmarked(result.bookmarked);
    } catch (error) {
      console.error('[VideoCard] Failed to toggle bookmark:', error);
    } finally {
      setIsTogglingBookmark(false);
    }
  };

  const handleClick = () => {
    // If custom onClick provided, use it
    if (onClick) {
      onClick({ id, videoId, title, courseName, videoUrl, lessonUrl, thumbnailUrl });
      return;
    }

    // Default behavior: Navigate to lesson URL if available, otherwise video URL
    const targetUrl = lessonUrl || videoUrl;
    if (targetUrl) {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className={styles.contentCard}
      onClick={handleClick}
      style={{ cursor: 'pointer' }}
    >
      {thumbnailUrl ? (
        <div className={styles.contentCardImage}>
          <img src={thumbnailUrl} alt={title} className={styles.image} />

          {/* Play button overlay */}
          <div className={videoStyles.playButtonOverlay}>
            <div className={videoStyles.playButton}>
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="24" cy="24" r="24" fill="rgba(255, 255, 255, 0.95)" />
                <path
                  d="M19 15L34 24L19 33V15Z"
                  fill="#1b4942"
                />
              </svg>
            </div>
          </div>

          {/* Course badge */}
          <div className={styles.contentCardBadge}>
            {courseName || 'Video'}
          </div>

          {/* Bookmark button */}
          {showBookmark && id && (
            <button
              className={styles.bookmarkButton}
              onClick={handleBookmarkClick}
              disabled={isTogglingBookmark}
              aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
            >
              <BookmarkIcon size={20} filled={isBookmarked} />
            </button>
          )}
        </div>
      ) : (
        // Fallback when no thumbnail
        <div className={styles.contentCardImage}>
          <div className={styles.imagePlaceholder}>
            <div className={videoStyles.playButton}>
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="24" cy="24" r="24" fill="rgba(27, 73, 66, 0.1)" />
                <path
                  d="M19 15L34 24L19 33V15Z"
                  fill="#6e8e87"
                />
              </svg>
            </div>
          </div>
          <div className={styles.contentCardBadge}>
            {courseName || 'Video'}
          </div>

          {/* Bookmark button */}
          {showBookmark && id && (
            <button
              className={styles.bookmarkButton}
              onClick={handleBookmarkClick}
              disabled={isTogglingBookmark}
              aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
            >
              <BookmarkIcon size={20} filled={isBookmarked} />
            </button>
          )}
        </div>
      )}

      <div className={styles.contentCardContent}>
        <div className={styles.contentCardTitle}>{title}</div>

        {description && (
          <div className={styles.contentCardDescription}>
            {description}
          </div>
        )}

        <div className={styles.contentCardMeta}>
          <div className={styles.contentCardMetaItem}>
            ▶ Watch Video
          </div>
        </div>
      </div>
    </div>
  );
}

export default VideoCard;
